import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface TimerState {
  isRunning: boolean
  taskId: string | null
  taskTitle: string | null
  startTime: string | null // ISO string for serialization
  elapsedSeconds: number

  // Actions
  startTimer: (taskId: string, taskTitle: string) => void
  stopTimer: () => { taskId: string; elapsedSeconds: number } | null
  tick: () => void
  reset: () => void
  hydrate: () => void // Restore timer state on page load
}

const STORAGE_KEY = 'copilot-core-timer'

export const useTimerStore = create<TimerState>()(
  persist(
    (set, get) => ({
      isRunning: false,
      taskId: null,
      taskTitle: null,
      startTime: null,
      elapsedSeconds: 0,

      startTimer: (taskId, taskTitle) => {
        // Stop any existing timer first
        const state = get()
        if (state.isRunning) {
          get().stopTimer()
        }

        set({
          isRunning: true,
          taskId,
          taskTitle,
          startTime: new Date().toISOString(),
          elapsedSeconds: 0,
        })
      },

      stopTimer: () => {
        const state = get()
        if (!state.isRunning || !state.taskId) return null

        const result = {
          taskId: state.taskId,
          elapsedSeconds: state.elapsedSeconds,
        }

        set({
          isRunning: false,
          taskId: null,
          taskTitle: null,
          startTime: null,
          elapsedSeconds: 0,
        })

        return result
      },

      tick: () => {
        const state = get()
        if (!state.isRunning || !state.startTime) return

        const startDate = new Date(state.startTime)
        const now = new Date()
        const elapsed = Math.floor((now.getTime() - startDate.getTime()) / 1000)
        set({ elapsedSeconds: elapsed })
      },

      reset: () =>
        set({
          isRunning: false,
          taskId: null,
          taskTitle: null,
          startTime: null,
          elapsedSeconds: 0,
        }),

      hydrate: () => {
        // Called on page load to recalculate elapsed time
        const state = get()
        if (state.isRunning && state.startTime) {
          const startDate = new Date(state.startTime)
          const now = new Date()
          const elapsed = Math.floor((now.getTime() - startDate.getTime()) / 1000)
          set({ elapsedSeconds: elapsed })
        }
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        isRunning: state.isRunning,
        taskId: state.taskId,
        taskTitle: state.taskTitle,
        startTime: state.startTime,
      }),
    }
  )
)

// Format seconds as HH:MM:SS
export function formatTimerDisplay(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  const pad = (n: number) => n.toString().padStart(2, '0')

  if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`
  }
  return `${pad(minutes)}:${pad(secs)}`
}
